import { Injectable, signal } from '@angular/core';

export interface ConfirmOptions {
  /** Optional heading shown above the confirmation message. */
  title?:         string;
  /** Main prompt text shown inside the confirmation modal. */
  message:        string;
  /** Optional item name highlighted in the modal body. */
  itemName?:      string;
  /** Label for the destructive/confirm action button. */
  confirmLabel?:  string;
  /** Label for the cancel action button. */
  cancelLabel?:   string;
}

/**
 * Shared promise-based confirmation modal state.
 *
 * Consumers call `confirm()` and await a boolean while the modal component binds
 * to this service's signals and calls `resolve()` for user actions.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmModalService {
  private resolver: ((confirmed: boolean) => void) | null = null;

  /** Whether the global confirmation modal should currently be visible. */
  readonly isOpen  = signal(false);
  /** Content and button labels for the active confirmation request. */
  readonly options = signal<ConfirmOptions>({ message: '' });

  /**
   * Opens the modal and returns the user's choice.
   *
   * @param opts Modal copy and button labels for this confirmation.
   * @returns `true` when confirmed, otherwise `false`.
   */
  confirm(opts: ConfirmOptions): Promise<boolean> {
    this.options.set(opts);
    this.isOpen.set(true);
    return new Promise(resolve => { this.resolver = resolve; });
  }

  /**
   * Completes the active confirmation request and hides the modal.
   *
   * @param confirmed Whether the user accepted the confirmation prompt.
   */
  resolve(confirmed: boolean): void {
    this.isOpen.set(false);
    this.resolver?.(confirmed);
    this.resolver = null;
  }
}
